import { Input, Select, TextArea } from '@/components/ui';
import type { CategoryFormValues, CategoryParentOption, CategoryType } from '../types/categories.types';

interface CategoryBasicInfoBlockProps {
  values: CategoryFormValues;
  parentOptions: CategoryParentOption[];
  onChange: <K extends keyof CategoryFormValues>(key: K, value: CategoryFormValues[K]) => void;
  isDisabled?: boolean;
}

const typeOptions: Array<{ value: CategoryType; label: string; icon: string }> = [
  { value: 'work', label: 'Công việc', icon: 'ri-task-line' },
  { value: 'material', label: 'Vật tư', icon: 'ri-hammer-line' },
  { value: 'equipment', label: 'Thiết bị', icon: 'ri-truck-line' },
  { value: 'personnel', label: 'Nhân sự', icon: 'ri-group-line' },
  { value: 'service', label: 'Dịch vụ', icon: 'ri-service-line' },
  { value: 'other', label: 'Khác', icon: 'ri-folder-3-line' },
];

export function CategoryBasicInfoBlock({ values, parentOptions, onChange, isDisabled }: CategoryBasicInfoBlockProps) {
  return (
    <section className="space-y-2 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
      <h2 className="text-sm text-slate-900 border-b border-slate-50 pb-2 mb-3">Thông tin cơ bản</h2>

      <div className="space-y-4">
        <label className="block space-y-1.5">
          <p className="text-xs text-slate-500 tracking-wider">Tên danh mục <span className="text-rose-500">*</span></p>
          <Input
            value={values.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="Nhập tên danh mục"
            disabled={isDisabled}
          />
        </label>

        <label className="block space-y-1.5">
          <p className="text-xs text-slate-500 tracking-wider">Mã danh mục</p>
          <Input
            value={values.code}
            onChange={(e) => onChange('code', e.target.value)}
            placeholder="Mã tự động tạo nếu bỏ trống"
            disabled={isDisabled}
          />
        </label>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block space-y-1.5">
            <p className="text-xs text-slate-500 tracking-wider">Phân loại</p>
            <Select
              value={values.type}
              onChange={(value) => {
                const type = value as CategoryType;
                const selected = typeOptions.find((item) => item.value === type);
                onChange('type', type);
                onChange('icon', selected?.icon || values.icon);
              }}
              options={typeOptions}
              disabled={isDisabled}
            />
          </label>

          <label className="block space-y-1.5">
            <p className="text-xs text-slate-500 tracking-wider">Trạng thái</p>
            <Select
              value={values.status}
              onChange={(value) => onChange('status', value as CategoryFormValues['status'])}
              options={[
                { value: 'active', label: 'Đang hoạt động' },
                { value: 'inactive', label: 'Tạm dừng' }
              ]}
              disabled={isDisabled}
            />
          </label>
        </div>

        <label className="block space-y-1.5">
          <p className="text-xs text-slate-500 tracking-wider">Danh mục cha</p>
          <Select
            value={values.parentId || ''}
            onChange={(value) => onChange('parentId', value || null)}
            options={[
              { value: '', label: '(Cấp cao nhất)' },
              ...parentOptions.map(item => ({ value: item.id, label: `${item.name} (${item.code})` }))
            ]}
            disabled={isDisabled}
          />
        </label>

        <label className="block space-y-1.5">
          <p className="text-xs text-slate-500 tracking-wider">Mô tả</p>
          <TextArea
            value={values.description}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder="Nhập mô tả danh mục"
            disabled={isDisabled}
            rows={4}
          />
        </label>
      </div>
    </section>
  );
}
